import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowUpRight, PieChart, Wallet, TrendingUp, ArrowDownLeft, Users, Briefcase } from "lucide-react";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const AGING_META = {
  green:    { label: "0 – 10 days",  className: "aging-badge-green" },
  yellow:   { label: "11 – 20 days", className: "aging-badge-yellow" },
  red:      { label: "21 – 30 days", className: "aging-badge-red" },
  critical: { label: "30+ days",     className: "aging-badge-critical" },
};

const AGING_STRIP = {
  green: "aging-strip-green",
  yellow: "aging-strip-yellow",
  red: "aging-strip-red",
  critical: "aging-strip-critical",
  paid: "aging-strip-paid",
};

function Slab({ to, kind, title, value, hint, icon: Icon, breakdown }) {
  return (
    <Link to={to} className={`slab ${kind} block animate-fade-up`} data-testid={`slab-${kind.replace('slab-','')}`}>
      <div className="flex items-start justify-between">
        <div className="slab-title">{title}</div>
        {Icon && <Icon size={18} strokeWidth={2} className="opacity-80" />}
      </div>
      <div className="slab-value">{value}</div>
      <div className="slab-hint">{hint}</div>
      {breakdown && (
        <div className="slab-breakdown" data-testid={`slab-breakdown-${kind.replace('slab-','')}`}>
          {breakdown.map((row) => (
            <div key={row.label} className="slab-breakdown-cell">
              <div className="slab-breakdown-label">{row.label}</div>
              <div className="slab-breakdown-value font-mono-num">{row.value}</div>
            </div>
          ))}
        </div>
      )}
    </Link>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [bills, setBills] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/stats"), api.get("/bills")]).then(([s, b]) => {
      setStats(s.data);
      setBills(b.data);
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const partials = bills.filter((b) => b.status === "partial").sort((a, b) => b.balance - a.balance).slice(0, 5);
  const recent = [...bills].slice(0, 5);
  const buckets = stats?.aging_buckets || {};

  return (
    <div className="p-8 lg:p-14 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between mb-12 animate-fade-up">
        <div>
          <div className="text-[11px] uppercase tracking-[0.35em] text-neutral-500">
            Overview
          </div>
          <h1 className="font-display text-6xl font-bold tracking-tight mt-2">
            Dashboard
          </h1>
        </div>
        <Link to="/bills/new" className="btn-primary py-3 text-base" data-testid="new-bill-btn">
          + New Bill
        </Link>
      </div>

      {/* Amount slabs row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6 stagger">
        <Slab
          to="/bills?filter=unpaid"
          kind="slab-outstanding"
          title="Outstanding"
          value={<span className="font-mono-num">{inr(stats?.total_outstanding)}</span>}
          hint="Yet to come in"
          icon={Wallet}
          breakdown={[
            { label: "Total", value: inr(stats?.total_billed) },
            { label: "Outstanding", value: inr(stats?.total_outstanding) },
            { label: "Received", value: inr(stats?.total_collected) },
            { label: "Fully paid", value: inr(stats?.paid_received) },
            { label: "Part paid", value: inr(stats?.partial_received) },
          ]}
        />
        <Slab
          to="/bills?filter=unpaid&doctype=bill"
          kind="slab-billed"
          title="Outstanding — Bills"
          value={<span className="font-mono-num">{inr(stats?.outstanding_bill)}</span>}
          hint="Unpaid on tax invoices"
          icon={TrendingUp}
          breakdown={[
            { label: "Total", value: inr(stats?.billed_bill) },
            { label: "Outstanding", value: inr(stats?.outstanding_bill) },
            { label: "Received", value: inr(stats?.collected_bill) },
          ]}
        />
        <Slab
          to="/bills?filter=unpaid&doctype=challan"
          kind="slab-received"
          title="Outstanding — Challans"
          value={<span className="font-mono-num">{inr(stats?.outstanding_challan)}</span>}
          hint="Unpaid on challans"
          icon={ArrowDownLeft}
          breakdown={[
            { label: "Total", value: inr(stats?.billed_challan) },
            { label: "Outstanding", value: inr(stats?.outstanding_challan) },
            { label: "Received", value: inr(stats?.collected_challan) },
          ]}
        />
      </div>

      {/* Secondary slabs row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10 stagger">
        <Slab
          to="/bills?filter=partial"
          kind="slab-reminders"
          title="Partial payments"
          value={<span className="font-mono-num">{inr(stats?.partial_received)}</span>}
          hint={`${stats?.partial_count ?? 0} ${stats?.partial_count === 1 ? "bill" : "bills"} part paid`}
          icon={PieChart}
          breakdown={[
            { label: "Received", value: inr(stats?.partial_received) },
            { label: "Pending", value: inr(stats?.partial_pending) },
          ]}
        />
        <Slab
          to="/buyers"
          kind="slab-buyers"
          title="Buyers"
          value={stats?.buyers_count ?? 0}
          hint="In your directory"
          icon={Users}
        />
        <Slab
          to="/employees"
          kind="slab-employees"
          title="Employees"
          value={stats?.employees_count ?? 0}
          hint="On team"
          icon={Briefcase}
        />
      </div>

      {/* Aging strip */}
      <div className="card-surface p-6 mb-10 animate-fade-up" data-testid="aging-panel">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl font-semibold">Aging</h2>
          <div className="text-xs text-neutral-500 uppercase tracking-widest">
            unpaid bills by days since bill
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger">
          {["green", "yellow", "red", "critical"].map((k) => {
            const m = AGING_META[k];
            const filterVal = k === "red" || k === "critical" ? "red" : "all";
            return (
              <Link
                key={k}
                to={`/bills?filter=${filterVal}`}
                className={`aging-badge ${m.className.replace('aging-badge-','aging-badge-')} !min-w-0`}
                style={{ padding: "22px 18px" }}
                data-testid={`aging-bucket-${k}`}
              >
                <div style={{ fontSize: 44, fontWeight: 800, fontFamily: "Bricolage Grotesque" }}>
                  {buckets[k] ?? 0}
                </div>
                <div style={{ marginTop: 8, opacity: 0.95, letterSpacing: "0.15em", fontSize: 11, fontWeight: 600 }}>
                  {m.label}
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent bills + Partial payments */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 stagger">
        <div className="lg:col-span-2 card-surface" data-testid="recent-bills">
          <div className="flex items-center justify-between p-6 border-b border-neutral-100">
            <h2 className="font-display text-2xl font-semibold">Recent Bills</h2>
            <Link to="/bills" className="text-xs text-neutral-500 hover:text-neutral-900 flex items-center gap-1">
              View all <ArrowUpRight size={12} />
            </Link>
          </div>
          <div>
            {recent.length === 0 && (
              <div className="p-8 text-sm text-neutral-500">No bills yet.</div>
            )}
            {recent.map((b) => (
              <Link
                key={b.id}
                to={`/bills/${b.id}/edit`}
                className="flex items-stretch gap-3 px-4 py-3 border-b border-neutral-100 last:border-0 hover:bg-[#FFF7ED] transition-colors"
              >
                <div className={`aging-strip ${AGING_STRIP[b.aging] || "aging-strip-green"}`} />
                <div className="flex-1 min-w-0 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-mono-num text-sm">{b.bill_number}</div>
                      <span className={b.doc_type === "challan" ? "badge-doctype-challan" : "badge-doctype-bill"}>
                        {b.doc_type === "challan" ? "Challan" : "Bill"}
                      </span>
                    </div>
                    <div className="text-xs text-neutral-500 truncate mt-0.5">
                      {b.buyer?.name} · via {b.employee?.name} · {b.bill_date}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {b.status === "paid" && <span className="badge-paid">Paid</span>}
                    {b.status === "partial" && <span className="badge-partial">Part</span>}
                    <div className="font-mono-num text-sm w-28 text-right">{inr(b.amount)}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="card-surface" data-testid="partial-panel">
          <div className="flex items-center justify-between gap-2 p-6 border-b border-neutral-100">
            <h2 className="font-display text-2xl font-semibold">Partial payments</h2>
            <Link to="/bills?filter=partial" className="text-xs text-neutral-500 hover:text-neutral-900 flex items-center gap-1" data-testid="partial-view-all">
              View all <ArrowUpRight size={12} />
            </Link>
          </div>
          <div>
            {partials.length === 0 && (
              <div className="p-6 text-sm text-neutral-500">No part-paid bills.</div>
            )}
            {partials.map((b) => (
              <Link
                key={b.id}
                to={`/bills/${b.id}/edit`}
                className="block px-6 py-4 border-b border-neutral-100 last:border-0 hover:bg-[#FFF7ED] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono-num text-sm">{b.bill_number}</div>
                  <div className="font-mono-num text-sm text-[#C4361B]">{inr(b.balance)} pending</div>
                </div>
                <div className="text-xs text-neutral-500">{b.buyer?.name} · received {inr(b.paid_amount)} of {inr(b.amount)}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
